import React, { useState, useEffect } from 'react';
import { UserPlus, Trash2, Edit2, Search } from 'lucide-react';
import { apiFetch } from '../utils/apiClient';
import { useNotification } from '../context/NotificationContext';
import { formatDateDDMMYYYY } from '../utils/formatDate';

const todayISO = () => new Date().toISOString().split('T')[0];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// off_days travels as a comma-separated string of JS day numbers ('0,6') to
// match how the column is stored; the form works with an array of numbers.
const parseOffDays = (raw) =>
  raw ? String(raw).split(',').map(Number).filter(n => !Number.isNaN(n)) : [];

const emptyForm = { name: '', email: '', phone: '', base_salary: '', join_date: todayISO(), device_user_id: '', shift_start: '09:00', shift_end: '18:00', off_days: [] };

const Employees = () => {
  const { notifySuccess, notifyError, showLoading, hideLoading } = useNotification();
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null); // employee being confirmed for deletion

  const fetchEmployees = async () => {
    try {
      const response = await apiFetch('/api/employees');
      if(response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (e) {
      console.error('Error fetching employees');
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const openAddForm = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (emp) => {
    setEditingId(emp.id);
    setFormData({
      name: emp.name || '',
      email: emp.email || '',
      phone: emp.phone || '',
      base_salary: emp.base_salary || '',
      join_date: emp.join_date ? new Date(emp.join_date).toISOString().split('T')[0] : '',
      device_user_id: emp.device_user_id || '',
      shift_start: emp.shift_start ? emp.shift_start.slice(0, 5) : '09:00',
      shift_end: emp.shift_end ? emp.shift_end.slice(0, 5) : '18:00',
      off_days: parseOffDays(emp.off_days)
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEditing = editingId !== null;
    showLoading(isEditing ? 'Saving changes…' : 'Adding employee…');
    try {
      const payload = { name: formData.name, email: formData.email, phone: formData.phone, base_salary: formData.base_salary, join_date: formData.join_date, device_user_id: formData.device_user_id, shift_start: formData.shift_start, shift_end: formData.shift_end, off_days: [...formData.off_days].sort((a, b) => a - b).join(',') };

      const response = await apiFetch(
        isEditing ? `/api/employees/${editingId}` : '/api/employees',
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      if(response.ok) {
        closeForm();
        fetchEmployees();
        notifySuccess(isEditing ? 'Employee updated successfully' : 'Employee added successfully');
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || 'Failed to save employee');
      }
    } catch(e) {
      notifyError('Failed to save employee');
    } finally {
      hideLoading();
    }
  };

  const openDeleteModal = (emp) => setDeleteTarget(emp);
  const closeDeleteModal = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    closeDeleteModal();
    showLoading('Deleting employee…');
    try {
      const response = await apiFetch(`/api/employees/${id}`, { method: 'DELETE' });
      if(response.ok) {
        fetchEmployees();
        notifySuccess('Employee deleted successfully');
      } else {
        notifyError('Failed to delete employee');
      }
    } catch(e) {
      notifyError('Failed to delete employee');
    } finally {
      hideLoading();
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  return (
    <div className="employees-page">
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700' }}>Manage Employees</h1>
        <button className="btn btn-primary" onClick={() => (showForm && editingId === null ? closeForm() : openAddForm())}>
          <UserPlus size={18} style={{ marginRight: '8px' }} />
          Add Employee
        </button>
      </div>

      <div style={{ position: 'relative', marginBottom: '24px', maxWidth: '360px' }}>
        <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input
          type="text"
          className="input-field"
          style={{ paddingLeft: '42px' }}
          placeholder="Search employee by name…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3>{editingId !== null ? 'Edit Employee' : 'Add New Employee'}</h3>
          <form className="form-grid-2col" style={{ marginTop: '16px' }} onSubmit={handleSubmit}>
            <div>
              <label className="label">Name</label>
              <input type="text" className="input-field" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
            </div>
            <div>
              <label className="label">Email</label>
              <input type="email" className="input-field" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
            </div>
            <div>
              <label className="label">Phone</label>
              <input type="text" className="input-field" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div>
              <label className="label">Base Salary</label>
              <input type="number" className="input-field" required value={formData.base_salary} onChange={e => setFormData({...formData, base_salary: e.target.value})} />
            </div>
            <div>
              <label className="label">Join Date</label>
              <input type="date" className="input-field" value={formData.join_date} onChange={e => setFormData({...formData, join_date: e.target.value})} />
            </div>
            <div>
              <label className="label">Device User ID (Fingerprint PIN)</label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. 1 — the PIN shown on the device"
                value={formData.device_user_id}
                onChange={e => setFormData({...formData, device_user_id: e.target.value})}
              />
            </div>
            <div>
              <label className="label">Shift Start</label>
              <input
                type="time"
                className="input-field"
                value={formData.shift_start}
                onChange={e => setFormData({...formData, shift_start: e.target.value})}
              />
            </div>
            <div>
              <label className="label">Shift End</label>
              <input
                type="time"
                className="input-field"
                value={formData.shift_end}
                onChange={e => setFormData({...formData, shift_end: e.target.value})}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="label">Weekly Off Days</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                {DAY_LABELS.map((label, dayNum) => {
                  const selected = formData.off_days.includes(dayNum);
                  return (
                    <button
                      key={dayNum}
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        off_days: selected
                          ? prev.off_days.filter(d => d !== dayNum)
                          : [...prev.off_days, dayNum]
                      }))}
                      style={{
                        padding: '8px 14px', fontSize: '13px', borderRadius: '8px', cursor: 'pointer',
                        border: `1px solid ${selected ? 'var(--primary-color)' : 'var(--border-color)'}`,
                        background: selected ? 'var(--primary-color)' : 'var(--surface-color)',
                        color: selected ? 'white' : 'var(--text-main)',
                        fontWeight: selected ? '600' : '400'
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                Days this employee is normally off. These are never counted as absent.
                Leave all unselected if they work every day.
              </p>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button type="button" className="btn" onClick={closeForm}>Cancel</button>
              <button type="submit" className="btn btn-primary">{editingId !== null ? 'Save Changes' : 'Save Employee'}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <div className="table-scroll">
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '640px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-color)', borderBottom: '1px solid var(--border-color)' }}>
              <th style={{ padding: '16px' }}>Name</th>
              <th style={{ padding: '16px' }}>Email</th>
              <th style={{ padding: '16px' }}>Phone</th>
              <th style={{ padding: '16px' }}>Join Date</th>
              <th style={{ padding: '16px' }}>Device ID</th>
              <th style={{ padding: '16px' }}>Shift</th>
              <th style={{ padding: '16px' }}>Weekly Off</th>
              <th style={{ padding: '16px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map(emp => (
              <tr key={emp.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '16px', fontWeight: '500' }}>{emp.name}</td>
                <td style={{ padding: '16px', color: 'var(--text-muted)' }}>{emp.email}</td>
                <td style={{ padding: '16px', color: 'var(--text-muted)' }}>{emp.phone}</td>
                <td style={{ padding: '16px', color: 'var(--text-muted)' }}>{emp.join_date ? formatDateDDMMYYYY(emp.join_date) : ''}</td>
                <td style={{ padding: '16px' }}>
                  {emp.device_user_id ? (
                    <span style={{ fontWeight: '500' }}>{emp.device_user_id}</span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Not linked</span>
                  )}
                </td>
                <td style={{ padding: '16px', color: 'var(--text-muted)', fontSize: '13px' }}>
                  {emp.shift_start && emp.shift_end ? `${emp.shift_start.slice(0, 5)} - ${emp.shift_end.slice(0, 5)}` : '—'}
                </td>
                <td style={{ padding: '16px', color: 'var(--text-muted)', fontSize: '13px' }}>
                  {parseOffDays(emp.off_days).length > 0
                    ? parseOffDays(emp.off_days).sort((a, b) => a - b).map(d => DAY_LABELS[d]).join(', ')
                    : <span style={{ fontSize: '12px' }}>None</span>}
                </td>
                <td style={{ padding: '16px', textAlign: 'right', display: 'flex', gap: '20px' }}>
                  <button className="icon-btn" style={{ color: 'var(--primary-color)', marginRight: '4px' }} onClick={() => openEditForm(emp)}>
                    <Edit2 size={18} />
                  </button>
                  <button className="icon-btn" style={{ color: 'var(--danger-color)' }} onClick={() => openDeleteModal(emp)}>
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
            {filteredEmployees.length === 0 && (
              <tr>
                <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  {employees.length === 0 ? 'No employees found. Add one to get started.' : `No employee found matching "${searchQuery}".`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {deleteTarget && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeDeleteModal}
        >
          <div className="card" style={{ width: '380px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '12px' }}>Delete Employee</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This will also permanently remove their salary records and payment history. This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button type="button" className="btn" onClick={closeDeleteModal}>Cancel</button>
              <button
                type="button"
                className="btn"
                style={{ background: 'var(--danger-color)', color: 'white' }}
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
